
import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RefreshCw, Wifi, WifiOff, AlertCircle, Globe, X } from 'lucide-react';
import { useAppContext } from '@/hooks/useAppContext';

interface RelayPropagationStatusProps {
  event: NostrEvent;
  addrCoord?: string;
  className?: string;
}

type RelayStatus = 'found' | 'not-found' | 'error' | 'checking';

// Public relays used only for discovery/wider scanning.
// Expanded for better coverage of where events may have propagated.
// These do NOT modify or sync with the user's NIP-65 relay list.
const DISCOVERY_RELAYS = [
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.nostr.band',
  'wss://nostr.wine',
  'wss://relay.ditto.pub',
  'wss://purple.pages.dev',
  'wss://relay.snort.social',
  'wss://eden.nostr.land',
  'wss://relay.current.fyi',
  'wss://nostr.rocks',
  'wss://relay.nostr.bg',
  'wss://nostr.mom',
  'wss://nostr.oxtr.dev',
  'wss://relay.bitcoiner.social',
  'wss://relay.nostr.wf',
  'wss://nostr-pub.wellorder.net',
  'wss://nostr.orangepill.dev',
] as const;

function useRelayCheck(event: NostrEvent, addrCoord: string | undefined, relays: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['relay-check', event.id, relays.join(','), enabled],
    enabled,
    queryFn: async () => {
      const results: Record<string, RelayStatus> = {};

      await Promise.all(
        relays.map(async (relayUrl) => {
          try {
            const ws = new WebSocket(relayUrl);

            const result = await new Promise<RelayStatus>((resolve) => {
              const timeout = setTimeout(() => {
                try { ws.close(); } catch { /* ignore */ }
                resolve('error');
              }, 7000);

              ws.onopen = () => {
                const subId = 'ts-' + Math.random().toString(36).slice(2, 8);
                let filter: Record<string, unknown>;

                if (addrCoord) {
                  const parts = addrCoord.split(':');
                  filter = {
                    kinds: [parseInt(parts[0])],
                    authors: [parts[1]],
                    '#d': [parts[2]],
                    limit: 1,
                  };
                } else {
                  filter = { ids: [event.id], limit: 1 };
                }

                ws.send(JSON.stringify(['REQ', subId, filter]));
              };

              ws.onmessage = (msg) => {
                try {
                  const data = JSON.parse(msg.data as string) as unknown[];
                  if (data[0] === 'EVENT') {
                    clearTimeout(timeout);
                    try { ws.close(); } catch { /* ignore */ }
                    resolve('found');
                  } else if (data[0] === 'EOSE') {
                    clearTimeout(timeout);
                    try { ws.close(); } catch { /* ignore */ }
                    resolve('not-found');
                  }
                } catch { /* ignore */ }
              };

              ws.onerror = () => {
                clearTimeout(timeout);
                resolve('error');
              };
            });

            results[relayUrl] = result;
          } catch {
            results[relayUrl] = 'error';
          }
        })
      );

      return results;
    },
    staleTime: 15000,
    retry: false,
  });
}

export function RelayPropagationStatus({ event, addrCoord, className }: RelayPropagationStatusProps) {
  const { config } = useAppContext();
  const [wideScan, setWideScan] = useState(false);
  const [customRelays, setCustomRelays] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState('');

  const userRelays = config.relayMetadata.relays.map((r) => r.url);
  const discoveryRelays = wideScan 
    ? [...DISCOVERY_RELAYS, ...customRelays] 
    : [];

  const { data: userStatuses, isFetching: userFetching, refetch: refetchUser } = useRelayCheck(
    event, 
    addrCoord, 
    userRelays, 
    true
  );

  const { data: discoveryStatuses, isFetching: discoveryFetching } = useRelayCheck(
    event, 
    addrCoord, 
    discoveryRelays, 
    wideScan
  );

  const isFetching = userFetching || discoveryFetching;

  const allStatuses = { ...userStatuses, ...discoveryStatuses };
  const statusCounts = allStatuses
    ? {
        found: Object.values(allStatuses).filter((s) => s === 'found').length,
        notFound: Object.values(allStatuses).filter((s) => s === 'not-found').length,
        error: Object.values(allStatuses).filter((s) => s === 'error').length,
      }
    : null;

  const handleRefresh = () => {
    refetchUser();
    // discovery will auto-refetch when enabled
  };

  const toggleWideScan = () => {
    setWideScan(!wideScan);
  };

  const addCustomRelay = () => {
    const trimmed = customInput.trim();
    if (!trimmed || customRelays.includes(trimmed)) return;
    
    // Basic validation for WebSocket URL
    if (!trimmed.startsWith('wss://') && !trimmed.startsWith('ws://')) {
      alert('Relay URL must start with wss:// or ws://');
      return;
    }

    setCustomRelays([...customRelays, trimmed]);
    setCustomInput('');
  };

  const removeCustomRelay = (url: string) => {
    setCustomRelays(customRelays.filter(r => r !== url));
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Wifi className="w-3.5 h-3.5" />
          <span>Relay propagation</span>
        </div>
        {statusCounts && (
          <span className="text-[10px] flex items-center gap-1">
            <span className="text-accent">{statusCounts.found} found</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{statusCounts.notFound} absent</span>
            {statusCounts.error > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-amber-400">{statusCounts.error} error</span>
              </>
            )}
          </span>
        )}
      </div>

      {/* User relays section */}
      <div className="space-y-1.5 animate-fade-in">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <span>Your relays</span>
            <span className="text-[10px] px-1.5 py-px bg-secondary rounded font-mono">
              {userRelays.length}
            </span>
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleWideScan}
              className="h-6 px-2 text-xs gap-1"
            >
              <Globe className="w-3 h-3" />
              {wideScan ? 'Hide public' : 'Scan wider'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              className="h-6 px-2 text-xs"
            >
              <RefreshCw className={cn('w-3 h-3 mr-1', isFetching && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        {userRelays.map((relay) => {
          const status: RelayStatus = isFetching 
            ? 'checking' 
            : (userStatuses?.[relay] ?? 'checking');
          return (
            <div key={relay} className="flex items-center gap-2.5 px-3 py-2 rounded bg-secondary/40 border border-border/50">
              <StatusDot status={status} />
              <span className="text-xs text-muted-foreground truncate flex-1 font-mono">
                {relay.replace(/^wss?:\/\//, '')}
              </span>
              <StatusLabel status={status} />
            </div>
          );
        })}
      </div>

      {/* Discovery / wider network section */}
      {wideScan && (
        <div className="space-y-1.5 animate-fade-in pt-3 border-t border-border/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              Custom Discovery Relays
              <span className="text-[10px] px-1.5 py-px bg-secondary rounded font-mono">
                {discoveryRelays.length}
              </span>
            </span>
          </div>

          {/* Custom relay input */}
          <div className="flex gap-2 mb-3">
            <Input
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="wss://your-custom-relay.com"
              className="text-xs h-8 font-mono bg-secondary/50"
              onKeyDown={(e) => {
                if (e.key === 'Enter') addCustomRelay();
              }}
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={addCustomRelay}
              className="h-8 px-3 text-xs whitespace-nowrap"
            >
              Add
            </Button>
          </div>

          {/* Discovery + custom relays list */}
          {discoveryRelays.map((relay) => {
            const isCustom = customRelays.includes(relay);
            const status: RelayStatus = isFetching 
              ? 'checking' 
              : (discoveryStatuses?.[relay] ?? 'checking');
            
            return (
              <div key={relay} className="flex items-center gap-2.5 px-3 py-2 rounded bg-secondary/30 border border-border/50 group">
                <StatusDot status={status} />
                <span className="text-xs text-muted-foreground truncate flex-1 font-mono">
                  {relay.replace(/^wss?:\/\//, '')}
                  {isCustom && <span className="ml-2 text-[9px] text-accent/70">(custom)</span>}
                </span>
                <StatusLabel status={status} />
                {isCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCustomRelay(relay)}
                    className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            );
          })}

          <p className="text-[10px] text-muted-foreground pl-1">
            These relays are used for discovery only and do not affect your NIP-65 relay list.
          </p>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: RelayStatus }) {
  const colors: Record<RelayStatus, string> = {
    found: 'bg-accent',
    'not-found': 'bg-muted-foreground',
    error: 'bg-amber-400',
    checking: 'bg-muted-foreground status-dot-pulse',
  };

  return <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', colors[status])} />;
}

function StatusLabel({ status }: { status: RelayStatus }) {
  if (status === 'found') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-[11px] text-accent font-medium">Found</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Event exists on this relay</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (status === 'not-found') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-[11px] text-muted-foreground">Absent</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Relay does not have this event</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (status === 'error') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-[11px] text-amber-400">Timeout</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Relay did not respond in time</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return <span className="text-[11px] text-muted-foreground">Checking…</span>;
}
